import type {
  EditorKey,
  EditorKeyEventFlags,
  EditorTextFeaturesConfig,
  EditorTextResult,
  EditorTextState,
} from 'src/modules/shared/types/editor/editor.type';

type ListDelimiter = '.' | ')';

interface ListLineParts {
  leading: string;
  numbers: number[] | null;
  delim: ListDelimiter | null;
  bullet: '-' | '•' | '*' | null;
  content: string;
  isList: boolean;
}

const LIST_LINE_REGEX = /^(\t|\s)*(?:(\d+(?:\.\d+)*)([.)])|([-•*]))\s*(.*)$/;

export const parseListLine = (line: string): ListLineParts => {
  const match = line.match(LIST_LINE_REGEX);
  if (!match) {
    return {
      leading: '',
      numbers: null,
      delim: null,
      bullet: null,
      content: line,
      isList: false,
    };
  }

  const leadingMatch = line.match(/^[\t ]*/) || [''];
  const leading = leadingMatch[0] ?? '';
  const numbersRaw = match[2] ?? '';
  const delim = (match[3] as ListDelimiter | undefined) ?? null;
  const bullet = (match[4] as '-' | '•' | '*' | undefined) ?? null;
  const content = (match[5] ?? '').trimStart();
  const numbers = numbersRaw
    ? numbersRaw
        .split('.')
        .map((n) => Number.parseInt(n, 10))
        .filter((n) => !Number.isNaN(n))
    : null;

  return {
    leading,
    numbers,
    delim,
    bullet,
    content,
    isList: true,
  };
};

export const indentBlock = (block: string): string =>
  block
    .split('\n')
    .map((line) => `\t${line}`)
    .join('\n');

export const outdentBlock = (block: string): string =>
  block
    .split('\n')
    .map((line) =>
      line.startsWith('\t')
        ? line.slice(1)
        : line.startsWith('    ')
          ? line.slice(4)
          : line
    )
    .join('\n');

export const convertNumberedToNestedBullet = (line: string): string | null => {
  const parts = parseListLine(line);
  if (!parts.numbers) return null;
  return `${parts.leading}\t- ${parts.content}`;
};

export const increaseBulletNesting = (line: string): string | null => {
  const parts = parseListLine(line);
  if (!parts.bullet) return null;
  const bullet = parts.bullet ?? '-';
  return `${parts.leading}\t${bullet} ${parts.content}`;
};

export const computeEnterInsertion = (
  line: string,
  caretAtLineStart: boolean
): string => {
  const parts = parseListLine(line);
  if (!parts.isList) return '\n';

  const hasContent = parts.content.trim().length > 0;
  if (!hasContent) {
    return `\n${parts.leading}`;
  }

  if (caretAtLineStart) {
    return '\n';
  }

  if (parts.bullet) {
    return `\n${parts.leading}${parts.bullet} `;
  }

  const delim = parts.delim ?? '.';
  if (!parts.numbers || parts.numbers.length === 0) {
    return `\n${parts.leading}1${delim} `;
  }

  const next = [...parts.numbers];
  const lastIndex = next.length - 1;
  next[lastIndex] = (next[lastIndex] ?? 0) + 1;

  return `\n${parts.leading}${next.join('.')}${delim} `;
};

const removeSingleIndent = (
  line: string
): { updated: string; removed: number } => {
  if (line.startsWith('\t')) {
    return { updated: line.slice(1), removed: 1 };
  }

  if (line.startsWith('    ')) {
    return { updated: line.slice(4), removed: 4 };
  }

  return { updated: line, removed: 0 };
};

export const applyEditorTextFeatures = (
  state: EditorTextState,
  key: EditorKey,
  flags: EditorKeyEventFlags,
  config: EditorTextFeaturesConfig
): EditorTextResult | null => {
  if (key !== 'Enter' && key !== 'Tab') return null;

  const { text } = state;
  const start = state.selection.start;
  const end = state.selection.end;
  const hasRange = start !== end;

  if (key === 'Enter') {
    if (hasRange) {
      if (!config.allowPlainNewline) return null;
      const insertion = '\n';
      const nextText = text.slice(0, start) + insertion + text.slice(end);
      const caret = start + insertion.length;
      return {
        text: nextText,
        selection: { start: caret, end: caret },
      };
    }

    const caret = start;
    const lineStart = text.lastIndexOf('\n', caret - 1) + 1;
    const lineEndIndex = text.indexOf('\n', caret);
    const lineEnd = lineEndIndex === -1 ? text.length : lineEndIndex;
    const line = text.slice(lineStart, lineEnd);

    const parts = parseListLine(line);
    const isListLine =
      parts.isList &&
      ((parts.bullet && config.bulletLists) ||
        (parts.numbers &&
          (config.numberedLists ||
            (config.nestedNumberedLists && parts.numbers.length > 1))));

    if (!isListLine || !config.continueListOnEnter) {
      if (!config.allowPlainNewline) return null;
      const insertion = '\n';
      const nextText = text.slice(0, caret) + insertion + text.slice(caret);
      const caretAfter = caret + insertion.length;
      return {
        text: nextText,
        selection: { start: caretAfter, end: caretAfter },
      };
    }

    const caretAtLineStart = caret === lineStart;
    const insertion = computeEnterInsertion(line, caretAtLineStart);
    const nextText = text.slice(0, caret) + insertion + text.slice(caret);
    const caretAfter = caret + insertion.length;

    return {
      text: nextText,
      selection: { start: caretAfter, end: caretAfter },
    };
  }

  if (key === 'Tab') {
    const selStart = text.lastIndexOf('\n', start - 1) + 1;
    const nextBreak = text.indexOf('\n', end);
    const selEnd = nextBreak === -1 ? text.length : nextBreak;

    if (flags.shiftKey) {
      if (hasRange) {
        if (!config.outdentWithShiftTab) return null;
        const block = text.slice(selStart, selEnd);
        const updated = outdentBlock(block);
        if (updated === block) return null;
        const nextText = text.slice(0, selStart) + updated + text.slice(selEnd);
        const delta = updated.length - block.length;
        return {
          text: nextText,
          selection: { start: selStart, end: selEnd + delta },
        };
      }

      if (!config.decreaseIndentOnShiftTab && !config.outdentWithShiftTab) {
        return null;
      }

      const lineStart = text.lastIndexOf('\n', start - 1) + 1;
      const lineEndIndex = text.indexOf('\n', start);
      const lineEnd = lineEndIndex === -1 ? text.length : lineEndIndex;
      const line = text.slice(lineStart, lineEnd);
      const { updated, removed } = removeSingleIndent(line);
      if (removed === 0) return null;
      const nextText = text.slice(0, lineStart) + updated + text.slice(lineEnd);
      const delta = updated.length - line.length;
      const newCaret =
        start <= lineStart ? lineStart : Math.max(lineStart, start + delta);

      return {
        text: nextText,
        selection: { start: newCaret, end: newCaret },
      };
    }

    if (hasRange) {
      if (!config.indentWithTab) {
        if (!config.allowPlainTab) return null;
        return null;
      }

      const block = text.slice(selStart, selEnd);
      const updated = indentBlock(block);
      if (updated === block) return null;
      const nextText = text.slice(0, selStart) + updated + text.slice(selEnd);
      const delta = updated.length - block.length;

      return {
        text: nextText,
        selection: { start: selStart, end: selEnd + delta },
      };
    }

    const lineStart = text.lastIndexOf('\n', start - 1) + 1;
    const lineEndIndex = text.indexOf('\n', start);
    const lineEnd = lineEndIndex === -1 ? text.length : lineEndIndex;
    const line = text.slice(lineStart, lineEnd);

    if (config.convertNumberedToNestedBulletOnTab) {
      const replacement = convertNumberedToNestedBullet(line);
      if (replacement) {
        const nextText =
          text.slice(0, lineStart) + replacement + text.slice(lineEnd);
        const delta = replacement.length - line.length;
        const newCaret = start + delta;
        return {
          text: nextText,
          selection: { start: newCaret, end: newCaret },
        };
      }
    }

    if (config.increaseBulletNestingOnTab) {
      const replacement = increaseBulletNesting(line);
      if (replacement) {
        const nextText =
          text.slice(0, lineStart) + replacement + text.slice(lineEnd);
        const delta = replacement.length - line.length;
        const newCaret = start + delta;
        return {
          text: nextText,
          selection: { start: newCaret, end: newCaret },
        };
      }
    }

    if (config.indentWithTab) {
      const nextText =
        text.slice(0, lineStart) + '\t' + line + text.slice(lineEnd);
      const newCaret = start + 1;
      return {
        text: nextText,
        selection: { start: newCaret, end: newCaret },
      };
    }

    if (config.allowPlainTab) {
      const nextText = text.slice(0, start) + '\t' + text.slice(end);
      const newCaret = start + 1;
      return {
        text: nextText,
        selection: { start: newCaret, end: newCaret },
      };
    }

    return null;
  }

  return null;
};
